import { useEffect } from 'react';

import { trackEvent } from '../utils/track';

const THRESHOLDS = [25, 50, 75, 100];

/**
 * Fires a "Scroll depth" event once per threshold per pageview.
 *
 * This is what tells you whether a bouncing visitor ever reached the social
 * proof, the comparison table or the bottom CTA — the difference between "the
 * message didn't land" and "they never saw the message".
 *
 * Renders nothing. Drop it into a page:
 *
 *   <ScrollDepth page="homepage" />
 *
 * Any other props are passed straight through as event properties, which is how
 * a percentage becomes comparable across pages of wildly different length:
 *
 *   <ScrollDepth page="blog-post" words={2400} slug="/blog/foo/" />
 *
 * Two things this deliberately does NOT do, both learned from production data:
 *
 *  1. It does not measure on mount. Gatsby's loadable components and images
 *     expand the page after hydration, so at mount the document can be barely
 *     taller than the viewport — which makes the computed depth ~100% and
 *     burns every threshold at once. The first version of this component did
 *     exactly that and fired all four events within 90ms of load.
 *
 *  2. It does not credit scroll position the user didn't produce. Chrome
 *     restores scroll position on reload, and anchor links jump mid-page. Both
 *     look identical to "read three quarters of the page" unless you baseline
 *     them out.
 *
 * So: nothing is reported until a real scroll event, and anything already
 * visible at load is marked as seen without being reported. Pageviews that
 * started part-way down carry a `resumedAt` property so they can be filtered
 * out of engagement analysis.
 */
const ScrollDepth = ({ page, ...context }) => {
  // Serialised so the effect isn't re-run on every render by a fresh object
  // identity. Callers pass literals, so this stays cheap.
  const contextKey = JSON.stringify(context);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const reached = new Set();
    const start = Date.now();
    // Scroll position is trustworthy at mount even when document height is not.
    const initialScrollY = window.scrollY;

    let frame = null;
    let hasScrolled = false;
    let baseline = null;

    /** Percentage of the document that has been seen, or null if unscrollable. */
    const depthAt = (scrollY) => {
      const docHeight = document.documentElement.scrollHeight;

      // Pages shorter than the viewport are fully visible on load. Reporting
      // that as "scrolled to 100%" would be meaningless.
      if (docHeight - window.innerHeight <= 0) return null;

      return ((scrollY + window.innerHeight) / docHeight) * 100;
    };

    /**
     * Establish what was already on screen when the page loaded, using the
     * settled document height at the time of first scroll rather than the
     * unsettled height at mount.
     */
    const seedBaseline = () => {
      baseline = depthAt(initialScrollY);
      if (baseline === null) return;

      THRESHOLDS.forEach((threshold) => {
        if (baseline >= threshold) reached.add(threshold);
      });
    };

    const measure = () => {
      frame = null;
      if (!hasScrolled) return;

      if (baseline === null) seedBaseline();

      const percent = depthAt(window.scrollY);
      if (percent === null) return;

      THRESHOLDS.forEach((threshold) => {
        if (percent < threshold || reached.has(threshold)) return;

        reached.add(threshold);

        const props = {
          page,
          depth: threshold,
          // Seconds since load, so a fast skim-and-leave is distinguishable
          // from someone reading their way down.
          seconds: Math.round((Date.now() - start) / 1000),
          ...context,
        };

        // Only present when the pageview began part-way down the document.
        if (baseline !== null && baseline > 15) props.resumedAt = Math.round(baseline);

        trackEvent('Scroll depth', props);
      });
    };

    const onScroll = () => {
      hasScrolled = true;
      if (frame !== null) return;
      frame = window.requestAnimationFrame(measure);
    };

    window.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [page, contextKey]);

  return null;
};

export default ScrollDepth;
